// BBALL SINGLES — standalone product screen. Two honest tiers per upcoming game:
//   • SHARP READ — the de-vigged sharp (Pinnacle, else market-consensus median)
//     fair's strongest call near the ~1.9 band, at the best real (non-exchange)
//     price. A READ, not proven edge — daily supply, entertainment-framed.
//   • EDGE — when a real book price beats the de-vigged Pinnacle (sharp) fair
//     (genuine +CLV). Badged so it stands out from the reads.
// Each row expands to show who the pick backs, the price source and an honest,
// derived reason. HONEST by construction: exchanges excluded, reads never dressed
// as edge, every pick PROVISIONAL until basketball realized CLV proves it.

import { useState } from "react";
import { BbHead, AwaitPanel, fmtOdds, fmtPct, fmtTip } from "./_bbkit.tsx";
import { SkeletonTableRows, Skeleton } from "../../components/skeletons/Skeleton.tsx";
import {
  useBballSingles, bballSingleMarketLabel, bballPickTarget, bballPickReason, bballFairOdds,
  type BballSinglePick,
} from "./useBballSingles.ts";

function TierBadge({ p }: { p: BballSinglePick }): JSX.Element {
  if (p.isEdge) {
    return (
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.1em",
        padding: "2px 7px", borderRadius: 4, color: "var(--bb-won)", background: "color-mix(in srgb, var(--bb-won) 14%, transparent)",
        border: "1px solid color-mix(in srgb, var(--bb-won) 40%, transparent)" }}>EDGE +{fmtPct(p.edgePct)}</span>
    );
  }
  return (
    <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.1em",
      padding: "2px 7px", borderRadius: 4, color: "var(--text-tertiary)", background: "var(--bg-inset)",
      border: "1px solid var(--border)" }}>SHARP READ</span>
  );
}

function Fact({ k, v, accent }: { k: string; v: string; accent?: boolean }): JSX.Element {
  return (
    <div className="bb-leg-fact">
      <div className="fk">{k}</div>
      <div className="fv" style={accent ? { color: "var(--bb-won)" } : undefined}>{v}</div>
    </div>
  );
}

function LegRow({ p, i, open, onToggle }: { p: BballSinglePick; i: number; open: boolean; onToggle: () => void }): JSX.Element {
  return (
    <div className="bb-leg-wrap">
      <div
        className="bb-legs-row bb-legs-row--btn"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      >
        <div className="idx">{i + 1}</div>
        <div>
          <div className="selection">
            <span className={`bb-chev${open ? " open" : ""}`} aria-hidden="true">▸</span>
            {p.home} v {p.away}
          </div>
          <div className="market">
            {bballSingleMarketLabel(p)} · {p.league ?? "NBA"}
            {p.kickoffAt ? ` · ${fmtTip(p.kickoffAt)}` : ""}
            {"  "}· best @ {p.bestBook}
            {p.anchor === "consensus" ? " · market read" : ""}
          </div>
        </div>
        <div style={{ textAlign: "center" }}><TierBadge p={p} /></div>
        <div className="odds">{fmtOdds(p.pinOdds)}</div>
        <div className="cum" style={p.isEdge ? { color: "var(--bb-won)" } : undefined}>{fmtOdds(p.softOdds)}</div>
      </div>

      {open ? (
        <div className="bb-leg-detail">
          <div className="bb-leg-pick">
            <span className="lbl">PICK</span>
            <span className="val">{bballPickTarget(p)}</span>
            <span className="at">@ {fmtOdds(p.softOdds)} · {p.bestBook}</span>
          </div>

          <p className="bb-leg-reason">{bballPickReason(p)}</p>

          <div className="bb-leg-facts">
            <Fact k="Backs" v={bballPickTarget(p)} />
            <Fact k="Fair odds" v={fmtOdds(bballFairOdds(p))} />
            <Fact k="Best price" v={fmtOdds(p.softOdds)} accent={p.isEdge} />
            <Fact k="Book" v={p.bestBook} />
            <Fact k="Source" v={p.anchor === "pinnacle" ? "Sharp (Pinnacle)" : `Consensus · ${p.numBooks} books`} />
            <Fact k="Implied chance" v={`${Math.round(p.fairProb * 100)}%`} />
            <Fact k="Tier" v={p.isEdge ? `Edge +${fmtPct(p.edgePct)}` : "Sharp read"} accent={p.isEdge} />
            <Fact k="Tip-off" v={p.kickoffAt ? fmtTip(p.kickoffAt) : "—"} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function BballSinglesScreen(): JSX.Element {
  const { loading, error, picks } = useBballSingles();
  const empty = !loading && (error != null || picks.length === 0);
  const edgeCount = picks.filter((p) => p.isEdge).length;
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="bb" data-screen-label="bball-singles">
      <BbHead kicker="BBALL SINGLES" title="Basketball" status="PROVISIONAL" accent="var(--bb-orange)" />

      {loading ? (
        <>
          <div className="bb-product-summary" style={{ marginBottom: 16 }}>
            <Skeleton width="60%" height={12} />
          </div>
          <SkeletonTableRows rows={4} cols={5} />
        </>
      ) : empty ? (
        <>
          {error ? (
            <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load BBALL SINGLES" body={error} ghosts={3} />
          ) : null}
          <div className="bb-await-grand">
            <div className="k">NO GAMES IN RANGE · BBALL SINGLES</div>
            <h3>One read per game — back when the next slate is priced.</h3>
            <p>
              BBALL SINGLES gives a sharp read on every upcoming game: the de-vigged
              sharp fair's strongest call near 1.9, at the best real price. There are
              no priced games in range right now, so there's nothing to read yet.
            </p>
            <div className="bb-ghost-stair" aria-hidden="true">
              {[28, 44, 60, 78].map((h, i) => (
                <div key={i} className="gh-step">
                  <div className="gh-bar" style={{ height: `${h}%` }} />
                  <div className="gh-lab">Game {i + 1}</div>
                </div>
              ))}
              <div className="gh-overlay">PREVIEW · not live</div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="bb-product-summary">
            <div className="head">
              SUMMARY
              <span className="t">
                {picks.length} sharp read{picks.length !== 1 ? "s" : ""} · one per game near 1.9
                {edgeCount > 0 ? ` · ${edgeCount} flagged EDGE (+CLV)` : " · no +CLV outlier right now"} · tap a row for the reason
              </span>
            </div>
            <div className="stat">GAMES<span className="v">{picks.length}</span></div>
            <div className="stat acc">EDGE PICKS<span className="v">{edgeCount}</span></div>
            <div className="stat">STATUS<span className="v" style={{ fontSize: 18, color: "var(--text-tertiary)" }}>PROVISIONAL</span></div>
          </div>

          <div className="bb-legs-table">
            <div className="bb-legs-header">
              <div>#</div>
              <div>GAME · PICK</div>
              <div style={{ textAlign: "center" }}>TIER</div>
              <div style={{ textAlign: "right" }}>FAIR</div>
              <div style={{ textAlign: "right" }}>BEST PRICE</div>
            </div>
            {picks.map((p, i) => (
              <LegRow key={p.fixtureId} p={p} i={i} open={open.has(p.fixtureId)} onToggle={() => toggle(p.fixtureId)} />
            ))}
          </div>

          <div style={{ marginTop: 16, fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-quaternary)", display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <span>SHARP READ = the de-vigged sharp fair's call near 1.9 (a read, not proven edge) · EDGE = best price beats the sharp line (genuine +CLV)</span>
            <span>PROVISIONAL · building CLV track record · manual placement only</span>
          </div>
        </>
      )}
    </div>
  );
}
